import {GlobalEventProvider, lazyInject} from "@dp-instant/core";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {TypesGame} from "@game/TypesGame";
import {injectable} from "inversify";
import {CookieModel} from "./CookieModel";
import {MetaConfig} from "./MetaConfig";

@injectable()
export class MetaModel extends GlobalEventProvider {
    @lazyInject(TypesGame.cookieModel)
    protected cookie: CookieModel;
    private _metaConfig: MetaConfig = new MetaConfig();
    public readonly loseMultiplier = 0.2;

    public getReward(level = this.cookie.absoluteLevelNum) {
        return this._metaConfig.GetLevelFinishReward(level);
    }
    public getBuildingPopulationLimit(owner: PlayerType): number {
        if (owner == PlayerType.Neutral)
            return this._metaConfig.GetNeutralCap(this.cookie.absoluteLevelNum);

        return this._metaConfig.GetCommonCap(this.cookie.absoluteLevelNum);
    }
    public getPopulationRate(owner: PlayerType): number {
        if (owner == PlayerType.Neutral)
            return this._metaConfig.GetNeutralRate(this.cookie.absoluteLevelNum);

        if (owner == PlayerType.First)
            return this.getPlayerGenerationRate(this.cookie.playerSpawnLevel);

        return this.getBotGenerationRate(this.cookie.absoluteLevelNum);
    }
    private getPlayerGenerationRate(level: int): float {
        return this._metaConfig.GetGenerationSpeed(level);
    }
    private getPerFightBotUpgrade(): number | undefined {
        const seq = (window as any).__earlyBotUpgrade as number[] | undefined;
        if (!seq) return undefined;
        const idx = (this.cookie.absoluteLevelNum - 1) * 4 + this.cookie.currentStage;
        return idx < seq.length ? seq[idx] : undefined;
    }

    private getBotGenerationRate(level: int) {
        const possibleMaxLevel = this._metaConfig.GetBotUpgradeLevelsCap(level, this.getPerFightBotUpgrade());
        return this._metaConfig.GetGenerationSpeed(possibleMaxLevel);
    }

    public getStartPopulation(owner: PlayerType): number {
        switch (owner) {
            case PlayerType.Neutral:
                return this._metaConfig.GetNeutralStartPopulation(this.cookie.absoluteLevelNum);
            case PlayerType.First:
                return this.getPlayerStartPopulation(this.cookie.playerStartPopulation);
        }

        return this.getBotStartPopulation(this.cookie.absoluteLevelNum);
    }
    private getPlayerStartPopulation(level: number): number {
        return this._metaConfig.GetStartPopulation(level);
    }
    private getBotStartPopulation(level: number): number {
        const possibleMaxLevel = this._metaConfig.GetBotUpgradeLevelsCap(level, this.getPerFightBotUpgrade());
        return this._metaConfig.GetStartPopulation(possibleMaxLevel);
    }

    public getStartPopulationCost(): float {
        return this._metaConfig.GetBoosterCost(this.cookie.playerStartPopulation);
    }

    public getPopulationRateCost(): float {
        return this._metaConfig.GetBoosterCost(this.cookie.playerSpawnLevel);
    }

    public getOfflineEarningCost(): float {
        return this._metaConfig.GetBoosterCost(this.cookie.playerOfflineLevel);
    }

    public getNextStartPopulation(owner: PlayerType): float {
        if (owner == PlayerType.Neutral)
            return this._metaConfig.GetNeutralStartPopulation(
                this.cookie.playerStartPopulation + 1
            );

        if (owner == PlayerType.First)
            return this.getPlayerStartPopulation(this.cookie.playerStartPopulation + 1);

        return this.getBotStartPopulation(this.cookie.absoluteLevelNum + 1);
    }

    public getNextPopulationRate(owner: PlayerType) {
        if (owner == PlayerType.Neutral)
            return this._metaConfig.GetNeutralRate(this.cookie.playerStartPopulation + 1);

        if (owner == PlayerType.First)
            return this.getPlayerGenerationRate(this.cookie.playerSpawnLevel + 1);

        return this.getBotGenerationRate(this.cookie.absoluteLevelNum + 1);
    }

    public getNextOfflineEarning(): float {
        return this._metaConfig.GetOfflineEarning(this.cookie.playerOfflineLevel + 1);
    }

    public getOfflineEarning(): float {
        return this._metaConfig.GetOfflineEarning(this.cookie.playerOfflineLevel);
    }

    public increaseStartPopulation(): void {
        var cost = this._metaConfig.GetBoosterCost(this.cookie.playerStartPopulation);
        if (this.cookie.isEnoughCoins(cost)) {
            this.cookie.playerStartPopulation++;
            this.cookie.spendCoins(cost);
            this.upgraded();
        }
    }

    public increaseStartPopulationFree(): void {
        this.cookie.playerStartPopulation++;
        this.upgradedForFree();
        this.upgraded();
    }

    public increaseSpawn(): void {
        const cost = this._metaConfig.GetBoosterCost(this.cookie.playerSpawnLevel);
        if (this.cookie.isEnoughCoins(cost)) {
            this.cookie.playerSpawnLevel++;
            this.cookie.spendCoins(cost);
            this.upgraded();
        }
    }

    public increaseSpawnFree(): void {
        this.cookie.playerSpawnLevel++;
        this.upgradedForFree();
        this.upgraded();
    }

    public increaseOffline(): void {
        const cost = this._metaConfig.GetBoosterCost(this.cookie.playerOfflineLevel);
        if (this.cookie.isEnoughCoins(cost)) {
            this.cookie.playerOfflineLevel++;
            this.cookie.spendCoins(cost);
            this.upgraded();
        }
    }

    public increaseOfflineFree(): void {
        this.cookie.playerOfflineLevel++;
        this.upgradedForFree();
        this.upgraded();
    }
    private upgradedForFree() {
        // UpgradedForFree?.Invoke();
    }
    private upgraded() {
        // Upgraded?.Invoke();
        if (__SOCIAL__ === "tg") (window as any).__syncCoins?.();
    }

    /**
     *
     * @description it's temporary method for provide speed value for booster buttons
     * @todo remove it after getPlayerGenerationRate fix
     */
    public getPlayerGenerationRateValue(level: int): float {
        return this._metaConfig.GetGenerationSpeed(level);
    }
}
